import {metadaApi, invalidateOnSuccess} from '@dms/api/base';

import {sleep} from 'shared/web';

import {
  DeleteServiceOrderItemsApiArg,
  DeleteServiceOrderItemsApiResponse,
  PostServiceOrderCalculatePriceBulkApiArg,
  PostServiceOrderCalculatePriceBulkApiResponse,
  GetOrderItemsTotalAmountApiArg,
  GetOrderItemsTotalAmountApiResponse,
  GetServiceOrderApiArg,
  GetServiceOrderApiResponse,
  GetServiceOrderCommercialApiArg,
  GetServiceOrderCommercialApiResponse,
  GetServiceOrderComplaintApiArg,
  GetServiceOrderComplaintApiResponse,
  GetServiceOrderContractApiArg,
  GetServiceOrderContractApiResponse,
  GetServiceOrderInsuranceApiArg,
  GetServiceOrderInsuranceApiResponse,
  GetServiceOrderInternalApiArg,
  GetServiceOrderInternalApiResponse,
  GetServiceOrderWarrantyApiArg,
  GetServiceOrderWarrantyApiResponse,
  PatchServiceOrderApiArg,
  PatchServiceOrderApiResponse,
  PatchServiceOrderCommercialApiArg,
  PatchServiceOrderCommercialApiResponse,
  PatchServiceOrderComplaintApiArg,
  PatchServiceOrderComplaintApiResponse,
  PatchServiceOrderContractApiArg,
  PatchServiceOrderContractApiResponse,
  PatchServiceOrderInsuranceApiArg,
  PatchServiceOrderInsuranceApiResponse,
  PatchServiceOrderInternalApiArg,
  PatchServiceOrderInternalApiResponse,
  PatchServiceOrderStateApiArg,
  PatchServiceOrderStateApiResponse,
  PatchServiceOrderWarrantyApiArg,
  PatchServiceOrderWarrantyApiResponse,
  PostServiceOrderApiArg,
  PostServiceOrderApiResponse,
  PostServiceOrderChangeVariantApiArg,
  PostServiceOrderChangeVariantApiResponse,
  PostServiceOrderChangeVariantCheckApiArg,
  PostServiceOrderChangeVariantCheckApiResponse,
  GetOrderProfitabilityApiArg,
  GetOrderProfitabilityApiResponse,
  PutServiceOrderIntegrationApiResponse,
  PutServiceOrderIntegrationApiArg,
  GetServiceOrderIntegrationsApiResponse,
  GetServiceOrderIntegrationsApiArg,
  GetServiceOrderTimeTrackingApiResponse,
  GetServiceOrderTimeTrackingApiArg,
  GetServiceOrderMandatoryFieldsApiArg,
  GetServiceOrderMandatoryFieldsApiResponse,
  PostServiceRequestCopyToServiceOrderApiArg,
  PostServiceRequestCopyToServiceOrderApiResponse,
  PostServiceItemsLabourSettingsApiArg,
  PostServiceItemsLabourSettingsApiResponse,
  PostServiceItemsMaterialPartRequestApiResponse,
  PostServiceItemsMaterialPartRequestApiArg,
  PostServiceItemsMaterialPartIssuedApiResponse,
  PostServiceItemsMaterialPartIssuedApiArg,
  GetServiceOrderTypesApiResponse,
  GetServiceOrderTypesApiArg,
  GetServiceOrderCheckoutRestrictionApiResponse,
  GetServiceOrderCheckoutRestrictionApiArg,
  PostServiceItemsMaterialPartRequestCancellationApiResponse,
  PostServiceItemsMaterialPartRequestCancellationApiArg,
  PostServiceItemsMaterialPartReturnApiResponse,
  PostServiceItemsMaterialPartReturnApiArg,
  GetServiceCaseServiceOrderMissingMandatoryFieldsApiResponse,
  GetServiceCaseServiceOrderMissingMandatoryFieldsApiArg,
  GetServiceCaseServiceOrderMandatoryFieldsV2ApiResponse,
  GetServiceCaseServiceOrderMandatoryFieldsV2ApiArg,
  GetPartMovementsCountApiResponse,
  GetPartMovementsCountApiArg,
  GetSmallUnitMaterialCustomRecalculationApiResponse,
  GetSmallUnitMaterialCustomRecalculationApiArg,
  PatchSmallUnitMaterialCustomRecalculationApiResponse,
  PatchSmallUnitMaterialCustomRecalculationApiArg,
  GetMinimumMarginCustomRecalculationApiResponse,
  GetMinimumMarginCustomRecalculationApiArg,
  PatchMinimumMarginCustomRecalculationApiResponse,
  PatchMinimumMarginCustomRecalculationApiArg,
  PatchMinimumMarginCurrentSettingsApiResponse,
  PatchMinimumMarginCurrentSettingsApiArg,
} from './types';

const WORKSHOP_SERVICE_ORDER_INVALIDATION_DELAY_MS = 2_500;

const getMinimumMarginInvalidatedTags = (
  queryArg: Pick<PatchMinimumMarginCustomRecalculationApiArg, 'serviceCaseId' | 'serviceOrderId'>
) =>
  [
    {type: 'serviceOrderMinimumMargin', id: queryArg.serviceOrderId},
    {type: 'serviceOrder', id: queryArg.serviceOrderId},
    {type: 'serviceOrders', id: queryArg.serviceCaseId},
    {type: 'serviceOrderJobs', id: queryArg.serviceOrderId},
    {type: 'serviceOrderTotalAmount', id: queryArg.serviceOrderId},
    {type: 'serviceCaseTotalAmount', id: queryArg.serviceCaseId},
    {type: 'orderProfitability', id: queryArg.serviceOrderId},
    'checkout',
    'afsCheckout',
    'afsPaymentsList',
    'afsPaymentByIdList',
  ] as const;

export const metadaWorkshopServiceOrderApi = metadaApi.injectEndpoints({
  endpoints: (build) => ({
    postServiceOrder: build.mutation<PostServiceOrderApiResponse, PostServiceOrderApiArg>({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order`,
        method: 'POST',
        body: queryArg.body,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'serviceOrders', id: queryArg.serviceCaseId},
        {type: 'serviceCase', id: queryArg.serviceCaseId},
        {type: 'serviceCaseOrderCount', id: queryArg.serviceCaseId},
      ],
    }),
    getServiceOrder: build.query<GetServiceOrderApiResponse, GetServiceOrderApiArg>({
      query: (queryArg) => ({
        url: `/service/v2/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'serviceOrder', id: queryArg.serviceOrderId},
        {type: 'serviceOrder', id: 'ALL'},
      ],
    }),
    patchServiceOrder: build.mutation<PatchServiceOrderApiResponse, PatchServiceOrderApiArg>({
      query: (queryArg) => ({
        url: `/service/v2/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: (result, error, queryArg) =>
        error
          ? []
          : [
              {type: 'serviceCase', id: queryArg.serviceCaseId},
              {type: 'serviceOrders', id: queryArg.serviceCaseId},
              {type: 'serviceOrder', id: queryArg.serviceOrderId},
              {type: 'orderProfitability', id: queryArg.serviceOrderId},
              {type: 'orderCustomerContract', id: queryArg.serviceOrderId},
              {type: 'serviceOrderCheckoutRestriction', id: queryArg.serviceOrderId},
              'afsPaymentIssueRestriction',
              'afsCheckout',
              'afsPaymentsList',
              'afsPaymentByIdList',
              {type: 'serviceCaseTotalAmount', id: queryArg.serviceCaseId},
              {type: 'orderActions', id: queryArg.serviceOrderId},
            ],
    }),

    getServiceOrderCommercial: build.query<
      GetServiceOrderCommercialApiResponse,
      GetServiceOrderCommercialApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/commercial`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'serviceOrderCommercial', id: queryArg.serviceOrderId},
      ],
    }),
    patchServiceOrderCommercial: build.mutation<
      PatchServiceOrderCommercialApiResponse,
      PatchServiceOrderCommercialApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/commercial`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'serviceOrderCommercial', id: queryArg.serviceOrderId},
      ],
    }),
    getServiceOrderComplaint: build.query<
      GetServiceOrderComplaintApiResponse,
      GetServiceOrderComplaintApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/complaint`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'serviceOrderComplaint', id: queryArg.serviceOrderId},
      ],
    }),
    patchServiceOrderComplaint: build.mutation<
      PatchServiceOrderComplaintApiResponse,
      PatchServiceOrderComplaintApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/complaint`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'serviceOrderComplaint', id: queryArg.serviceOrderId},
      ],
    }),
    getServiceOrderContract: build.query<
      GetServiceOrderContractApiResponse,
      GetServiceOrderContractApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/contract`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'serviceOrderContract', id: queryArg.serviceOrderId},
      ],
    }),
    patchServiceOrderContract: build.mutation<
      PatchServiceOrderContractApiResponse,
      PatchServiceOrderContractApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/contract`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'serviceOrderContract', id: queryArg.serviceOrderId},
        {type: 'orderCustomerContract', id: queryArg.serviceOrderId},
      ],
    }),
    deleteServiceOrderItems: build.mutation<
      DeleteServiceOrderItemsApiResponse,
      DeleteServiceOrderItemsApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/delete-items`,
        method: 'POST',
        body: queryArg.body,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'serviceOrders', id: queryArg.serviceCaseId},
        {type: 'serviceOrder', id: queryArg.serviceOrderId},
        {type: 'serviceOrderJobs', id: queryArg.serviceOrderId},
        {type: 'serviceOrderTotalAmount', id: queryArg.serviceOrderId},
        {type: 'serviceCase', id: queryArg.serviceCaseId},
        {type: 'serviceCaseTotalAmount', id: queryArg.serviceCaseId},
        'serviceOrderJobTotalAmount',
        {type: 'orderProfitability', id: queryArg.serviceOrderId},
        {type: 'serviceOrderJobs', id: queryArg.serviceOrderId},
        'checkout',
        'afsCheckout',
        'afsPaymentsList',
        'afsPaymentByIdList',
        'serviceJobActions',
        {type: 'serviceActions', id: queryArg.serviceCaseId},
        {type: 'orderActions', id: queryArg.serviceOrderId},
        {type: 'serviceOrderCheckoutRestriction', id: queryArg.serviceOrderId},
        'afsPaymentIssueRestriction',
      ],
    }),
    postServiceOrderCalculatePriceBulk: build.mutation<
      PostServiceOrderCalculatePriceBulkApiResponse,
      PostServiceOrderCalculatePriceBulkApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/items/calculate-price`,
        method: 'POST',
        body: queryArg.body,
      }),
      invalidatesTags: invalidateOnSuccess(
        (_result, queryArg: PostServiceOrderCalculatePriceBulkApiArg) => [
          {type: 'serviceOrders', id: queryArg.serviceCaseId},
          {type: 'serviceOrder', id: queryArg.serviceOrderId},
          {type: 'serviceOrderJobs', id: queryArg.serviceOrderId},
          {type: 'serviceOrderTotalAmount', id: queryArg.serviceOrderId},
          {type: 'serviceCaseTotalAmount', id: queryArg.serviceCaseId},
          {type: 'orderProfitability', id: queryArg.serviceOrderId},
          'serviceOrderJobTotalAmount',
          'checkout',
          'afsCheckout',
          'afsPaymentsList',
          'afsPaymentByIdList',
          {type: 'serviceOrderCheckoutRestriction', id: queryArg.serviceOrderId},
          'afsPaymentIssueRestriction',
        ]
      ),
    }),
    getServiceOrderInsurance: build.query<
      GetServiceOrderInsuranceApiResponse,
      GetServiceOrderInsuranceApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v2/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/insurance`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'serviceOrderInsurance', id: queryArg.serviceOrderId},
      ],
    }),
    patchServiceOrderInsurance: build.mutation<
      PatchServiceOrderInsuranceApiResponse,
      PatchServiceOrderInsuranceApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v2/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/insurance`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'serviceOrderInsurance', id: queryArg.serviceOrderId},
        {type: 'orderCustomerContract', id: queryArg.serviceOrderId},
      ],
    }),
    putServiceOrderIntegration: build.mutation<
      PutServiceOrderIntegrationApiResponse,
      PutServiceOrderIntegrationApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/integration`,
        method: 'PUT',
        body: queryArg.body,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'orderIntegration', id: queryArg.serviceOrderId},
        {type: 'serviceOrderJobs', id: queryArg.serviceOrderId},
        {type: 'serviceOrderTotalAmount', id: queryArg.serviceOrderId},
        {type: 'orderProfitability', id: queryArg.serviceOrderId},
        {type: 'serviceCase', id: queryArg.serviceCaseId},
        {type: 'serviceCaseTotalAmount', id: queryArg.serviceCaseId},
        {type: 'serviceOrder', id: queryArg.serviceOrderId},
        {type: 'serviceOrders', id: queryArg.serviceCaseId},
        'serviceOrderJobTotalAmount',
        'checkout',
        'afsCheckout',
        'afsPaymentsList',
        'afsPaymentByIdList',
      ],
    }),
    getServiceOrderIntegrations: build.query<
      GetServiceOrderIntegrationsApiResponse,
      GetServiceOrderIntegrationsApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/integrations`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'orderIntegration', id: queryArg.serviceOrderId},
      ],
    }),
    getServiceOrderInternal: build.query<
      GetServiceOrderInternalApiResponse,
      GetServiceOrderInternalApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/internal`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'serviceOrderInternal', id: queryArg.serviceOrderId},
      ],
    }),
    patchServiceOrderInternal: build.mutation<
      PatchServiceOrderInternalApiResponse,
      PatchServiceOrderInternalApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/internal`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'serviceOrderInternal', id: queryArg.serviceOrderId},
      ],
    }),
    getOrderItemsTotalAmount: build.query<
      GetOrderItemsTotalAmountApiResponse,
      GetOrderItemsTotalAmountApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/items-total-amount`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'serviceOrderTotalAmount', id: queryArg.serviceOrderId},
      ],
    }),
    getOrderProfitability: build.query<
      GetOrderProfitabilityApiResponse,
      GetOrderProfitabilityApiArg
    >({
      query: (queryArg) => ({
        url: `service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/profitability`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'orderProfitability', id: queryArg.serviceOrderId},
      ],
    }),
    patchServiceOrderState: build.mutation<
      PatchServiceOrderStateApiResponse,
      PatchServiceOrderStateApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/state`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'serviceOrder', id: queryArg.serviceOrderId},
        'checkout',
        'afsCheckout',
        'afsPaymentsList',
        'afsPaymentByIdList',
        {type: 'serviceOrderCheckoutRestriction', id: queryArg.serviceOrderId},
        'afsPaymentIssueRestriction',
      ],
    }),
    getServiceOrderTimeTracking: build.query<
      GetServiceOrderTimeTrackingApiResponse,
      GetServiceOrderTimeTrackingApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/time-tracking`,
        params: {serviceCaseJobId: queryArg.serviceCaseJobId},
      }),
    }),
    postServiceOrderChangeVariant: build.mutation<
      PostServiceOrderChangeVariantApiResponse,
      PostServiceOrderChangeVariantApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/variant-change`,
        method: 'POST',
        params: {orderVariantId: queryArg.orderVariantId},
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'serviceOrders', id: queryArg.serviceCaseId},
        {type: 'serviceOrder', id: queryArg.serviceOrderId},
        {type: 'serviceOrderJobs', id: queryArg.serviceOrderId},
        {type: 'orderDiscount', id: queryArg.serviceOrderId},
        {type: 'serviceOrderJobTotalAmount', id: queryArg.serviceOrderId},
        {type: 'serviceOrderTotalAmount', id: queryArg.serviceOrderId},
        {type: 'orderProfitability', id: queryArg.serviceOrderId},
        'checkout',
        'afsCheckout',
        'afsPaymentsList',
        'afsPaymentByIdList',
        'afsPaymentTemplates',
        {type: 'serviceOrderCommercial', id: queryArg.serviceOrderId},
        {type: 'serviceOrderComplaint', id: queryArg.serviceOrderId},
        {type: 'serviceOrderContract', id: queryArg.serviceOrderId},
        {type: 'serviceOrderInsurance', id: queryArg.serviceOrderId},
        {type: 'serviceOrderInternal', id: queryArg.serviceOrderId},
        {type: 'serviceOrderWarranty', id: queryArg.serviceOrderId},
        {type: 'serviceCaseTotalAmount', id: queryArg.serviceCaseId},
        {type: 'orderCustomerContract', id: queryArg.serviceOrderId},
        {type: 'orderActions', id: queryArg.serviceOrderId},
        {type: 'serviceOrderCheckoutRestriction', id: queryArg.serviceOrderId},
        'afsPaymentIssueRestriction',
        {type: 'serviceCase', id: queryArg.serviceCaseId},
        {type: 'afsBillingInformation'},
        'afsAllowedCustomer',
      ],
    }),
    postServiceOrderChangeVariantCheck: build.query<
      PostServiceOrderChangeVariantCheckApiResponse,
      PostServiceOrderChangeVariantCheckApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/variant-change-allowed`,
      }),
    }),
    getServiceOrderWarranty: build.query<
      GetServiceOrderWarrantyApiResponse,
      GetServiceOrderWarrantyApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/warranty`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'serviceOrderWarranty', id: queryArg.serviceOrderId},
      ],
    }),
    patchServiceOrderWarranty: build.mutation<
      PatchServiceOrderWarrantyApiResponse,
      PatchServiceOrderWarrantyApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/warranty`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'serviceOrderWarranty', id: queryArg.serviceOrderId},
      ],
    }),
    getServiceOrderMandatoryFields: build.query<
      GetServiceOrderMandatoryFieldsApiResponse,
      GetServiceOrderMandatoryFieldsApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceOrderId}/mandatory-fields`,
        params: {event: queryArg.event},
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'serviceOrderMandatoryFields', id: queryArg.serviceOrderId},
      ],
    }),
    postServiceRequestCopyToServiceOrder: build.mutation<
      PostServiceRequestCopyToServiceOrderApiResponse,
      PostServiceRequestCopyToServiceOrderApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/service-request-copy`,
        method: 'POST',
        body: queryArg.body,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'serviceOrders', id: queryArg.serviceCaseId},
        {type: 'serviceOrder', id: queryArg.serviceOrderId},
        {type: 'serviceOrderJobs', id: queryArg.serviceOrderId},
        {type: 'serviceOrderTotalAmount', id: queryArg.serviceOrderId},
        {type: 'serviceCase', id: queryArg.serviceCaseId},
        {type: 'serviceCaseTotalAmount', id: queryArg.serviceCaseId},
        {type: 'orderProfitability', id: queryArg.serviceOrderId},
        'checkout',
        'afsCheckout',
        'afsPaymentsList',
        'afsPaymentByIdList',
        {type: 'serviceOrderCheckoutRestriction', id: queryArg.serviceOrderId},
        'afsPaymentIssueRestriction',
      ],
    }),
    postServiceItemsLabourSettings: build.mutation<
      PostServiceItemsLabourSettingsApiResponse,
      PostServiceItemsLabourSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/item-settings/labour`,
        method: 'POST',
        body: queryArg.body,
      }),
    }),
    postServiceItemsMaterialPartRequest: build.mutation<
      PostServiceItemsMaterialPartRequestApiResponse,
      PostServiceItemsMaterialPartRequestApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/material-items/request`,
        method: 'POST',
        body: queryArg.body,
      }),
      async onQueryStarted(queryArg, api) {
        try {
          await api.queryFulfilled;
        } catch {}

        await sleep(WORKSHOP_SERVICE_ORDER_INVALIDATION_DELAY_MS);
        api.dispatch(
          metadaWorkshopServiceOrderApi.util.invalidateTags([
            'serviceOrderJobTotalAmount',
            'checkout',
            'afsCheckout',
            'afsPaymentsList',
            'afsPaymentByIdList',
            'serviceJobActions',
            'afsPaymentIssueRestriction',
            'serviceJobs',
            'serviceOrders',
            'serviceCase',
            {type: 'serviceOrders', id: queryArg.serviceCaseId},
            {type: 'serviceOrder', id: queryArg.serviceOrderId},
            {type: 'serviceOrderJobs', id: queryArg.serviceOrderId},
            {type: 'serviceJobs', id: queryArg.serviceCaseId},
            {type: 'serviceOrderTotalAmount', id: queryArg.serviceOrderId},
            {type: 'serviceCase', id: queryArg.serviceCaseId},
            {type: 'serviceCaseTotalAmount', id: queryArg.serviceCaseId},
            {type: 'orderProfitability', id: queryArg.serviceOrderId},
            {type: 'serviceOrderJobs', id: queryArg.serviceOrderId},
            {type: 'serviceActions', id: queryArg.serviceCaseId},
            {type: 'orderActions', id: queryArg.serviceOrderId},
            {type: 'serviceOrderCheckoutRestriction', id: queryArg.serviceOrderId},
            {type: 'pendingServiceItems', id: queryArg.serviceCaseId},
            {type: 'pendingServiceOrderReturn', id: queryArg.serviceCaseId},
          ])
        );
      },
    }),

    postServiceItemsMaterialPartRequestCancellation: build.mutation<
      PostServiceItemsMaterialPartRequestCancellationApiResponse,
      PostServiceItemsMaterialPartRequestCancellationApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/material-items/request-cancellation`,
        method: 'POST',
        body: queryArg.body,
      }),
      async onQueryStarted(queryArg, api) {
        try {
          await api.queryFulfilled;
        } catch {}

        await sleep(WORKSHOP_SERVICE_ORDER_INVALIDATION_DELAY_MS);
        api.dispatch(
          metadaWorkshopServiceOrderApi.util.invalidateTags([
            {type: 'serviceOrders', id: queryArg.serviceCaseId},
            {type: 'serviceOrder', id: queryArg.serviceOrderId},
            {type: 'serviceOrderJobs', id: queryArg.serviceOrderId},
            {type: 'serviceOrderTotalAmount', id: queryArg.serviceOrderId},
            {type: 'serviceCase', id: queryArg.serviceCaseId},
            {type: 'serviceCaseTotalAmount', id: queryArg.serviceCaseId},
            'serviceOrderJobTotalAmount',
            {type: 'orderProfitability', id: queryArg.serviceOrderId},
            {type: 'serviceOrderJobs', id: queryArg.serviceOrderId},
            'checkout',
            'afsCheckout',
            'afsPaymentsList',
            'afsPaymentByIdList',
            'serviceJobActions',
            {type: 'serviceJobs', id: queryArg.serviceCaseId},
            {type: 'serviceActions', id: queryArg.serviceCaseId},
            {type: 'orderActions', id: queryArg.serviceOrderId},
            {type: 'serviceOrderCheckoutRestriction', id: queryArg.serviceOrderId},
            'afsPaymentIssueRestriction',
            {type: 'pendingServiceItems', id: queryArg.serviceCaseId},
            {type: 'pendingServiceOrderReturn', id: queryArg.serviceCaseId},
          ])
        );
      },
    }),
    postServiceItemsMaterialPartIssued: build.mutation<
      PostServiceItemsMaterialPartIssuedApiResponse,
      PostServiceItemsMaterialPartIssuedApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v2/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/material-items/issue-note`,
        method: 'POST',
        body: queryArg.body,
      }),
      async onQueryStarted(queryArg, api) {
        try {
          await api.queryFulfilled;
        } catch {}

        await sleep(WORKSHOP_SERVICE_ORDER_INVALIDATION_DELAY_MS);
        api.dispatch(
          metadaWorkshopServiceOrderApi.util.invalidateTags([
            'serviceOrders',
            'serviceCase',
            'serviceOrderJobTotalAmount',
            'checkout',
            'afsCheckout',
            'afsPaymentsList',
            'afsPaymentByIdList',
            'serviceJobActions',
            'afsPaymentIssueRestriction',
            {type: 'serviceOrders', id: queryArg.serviceCaseId},
            {type: 'serviceOrder', id: queryArg.serviceOrderId},
            {type: 'serviceOrderJobs', id: queryArg.serviceOrderId},
            {type: 'serviceOrderTotalAmount', id: queryArg.serviceOrderId},
            {type: 'serviceCase', id: queryArg.serviceCaseId},
            {type: 'serviceCaseTotalAmount', id: queryArg.serviceCaseId},
            {type: 'orderProfitability', id: queryArg.serviceOrderId},
            {type: 'serviceOrderJobs', id: queryArg.serviceOrderId},
            {type: 'serviceActions', id: queryArg.serviceCaseId},
            {type: 'orderActions', id: queryArg.serviceOrderId},
            {type: 'serviceOrderCheckoutRestriction', id: queryArg.serviceOrderId},
            {type: 'pendingServiceItems', id: queryArg.serviceCaseId},
            {type: 'pendingServiceOrderReturn', id: queryArg.serviceCaseId},
          ])
        );
      },
    }),
    getServiceOrderTypes: build.query<GetServiceOrderTypesApiResponse, GetServiceOrderTypesApiArg>({
      query: () => ({
        url: `/service/v1/service-order-types`,
      }),
    }),
    getServiceOrderCheckoutRestriction: build.query<
      GetServiceOrderCheckoutRestrictionApiResponse,
      GetServiceOrderCheckoutRestrictionApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/checkout-restriction`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'serviceOrderCheckoutRestriction', id: queryArg.serviceOrderId},
      ],
    }),
    postServiceItemsMaterialPartReturn: build.mutation<
      PostServiceItemsMaterialPartReturnApiResponse,
      PostServiceItemsMaterialPartReturnApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/material-items/return`,
        method: 'POST',
        body: queryArg.body,
      }),
      async onQueryStarted(queryArg, api) {
        try {
          await api.queryFulfilled;
        } catch {}

        await sleep(WORKSHOP_SERVICE_ORDER_INVALIDATION_DELAY_MS);
        api.dispatch(
          metadaWorkshopServiceOrderApi.util.invalidateTags([
            {type: 'serviceOrders', id: queryArg.serviceCaseId},
            {type: 'serviceOrder', id: queryArg.serviceOrderId},
            {type: 'serviceOrderJobs', id: queryArg.serviceOrderId},
            {type: 'serviceOrderTotalAmount', id: queryArg.serviceOrderId},
            {type: 'serviceCase', id: queryArg.serviceCaseId},
            {type: 'serviceCaseTotalAmount', id: queryArg.serviceCaseId},
            'serviceOrderJobTotalAmount',
            {type: 'orderProfitability', id: queryArg.serviceOrderId},
            {type: 'serviceOrderJobs', id: queryArg.serviceOrderId},
            'checkout',
            'afsCheckout',
            'afsPaymentsList',
            'afsPaymentByIdList',
            'serviceJobActions',
            {type: 'serviceActions', id: queryArg.serviceCaseId},
            {type: 'orderActions', id: queryArg.serviceOrderId},
            {type: 'serviceOrderCheckoutRestriction', id: queryArg.serviceOrderId},
            'afsPaymentIssueRestriction',
            {type: 'pendingServiceOrderReturn', id: queryArg.serviceCaseId},
            {type: 'pendingServiceItems', id: queryArg.serviceCaseId},
          ])
        );
      },
    }),
    getServiceCaseServiceOrderMissingMandatoryFieldsApi: build.query<
      GetServiceCaseServiceOrderMissingMandatoryFieldsApiResponse,
      GetServiceCaseServiceOrderMissingMandatoryFieldsApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/missing-mandatory-fields`,
        params: {event: queryArg.event},
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'missingServiceOrderMandatoryFields', id: queryArg.serviceOrderId},
      ],
    }),
    getServiceCaseServiceOrderMandatoryFieldsV2Api: build.query<
      GetServiceCaseServiceOrderMandatoryFieldsV2ApiResponse,
      GetServiceCaseServiceOrderMandatoryFieldsV2ApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v2/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/mandatory-fields`,
        params: {event: queryArg.event},
      }),
    }),
    getPartMovementsCount: build.query<
      GetPartMovementsCountApiResponse,
      GetPartMovementsCountApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/part-movements`,
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'partMovementsCount', id: queryArg.serviceOrderId},
      ],
    }),
    getSmallUnitMaterialCustomRecalculation: build.query<
      GetSmallUnitMaterialCustomRecalculationApiResponse,
      GetSmallUnitMaterialCustomRecalculationApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/small-unit-material`,
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'serviceOrderSmallUnitMaterial', id: queryArg.serviceOrderId},
        // Subscription, not a payload claim: recalculated server-side from the order's items.
        {type: 'serviceOrderTotalAmount', id: queryArg.serviceOrderId},
      ],
    }),
    patchSmallUnitMaterialCustomRecalculation: build.mutation<
      PatchSmallUnitMaterialCustomRecalculationApiResponse,
      PatchSmallUnitMaterialCustomRecalculationApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/small-unit-material`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: invalidateOnSuccess(
        (_result, queryArg: PatchSmallUnitMaterialCustomRecalculationApiArg) => [
          {type: 'serviceOrderSmallUnitMaterial', id: queryArg.serviceOrderId},
          {type: 'serviceOrders', id: queryArg.serviceCaseId},
          {type: 'serviceOrder', id: queryArg.serviceOrderId},
          {type: 'serviceOrderJobs', id: queryArg.serviceOrderId},
          {type: 'serviceOrderTotalAmount', id: queryArg.serviceOrderId},
          {type: 'serviceOrderJobTotalAmount', id: queryArg.serviceOrderId},
          {type: 'serviceCaseTotalAmount', id: queryArg.serviceCaseId},
          {type: 'orderProfitability', id: queryArg.serviceOrderId},
          'checkout',
          'afsCheckout',
          'afsPaymentsList',
          'afsPaymentByIdList',
        ]
      ),
    }),
    getMinimumMarginCustomRecalculation: build.query<
      GetMinimumMarginCustomRecalculationApiResponse,
      GetMinimumMarginCustomRecalculationApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/minimum-margin`,
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'serviceOrderMinimumMargin', id: queryArg.serviceOrderId},
        // Subscription, not a payload claim: recalculated server-side from the order's items.
        {type: 'serviceOrderTotalAmount', id: queryArg.serviceOrderId},
      ],
    }),
    patchMinimumMarginCustomRecalculation: build.mutation<
      PatchMinimumMarginCustomRecalculationApiResponse,
      PatchMinimumMarginCustomRecalculationApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/minimum-margin`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: invalidateOnSuccess(
        (_result, queryArg: PatchMinimumMarginCustomRecalculationApiArg) =>
          getMinimumMarginInvalidatedTags(queryArg)
      ),
    }),
    patchMinimumMarginCurrentSettings: build.mutation<
      PatchMinimumMarginCurrentSettingsApiResponse,
      PatchMinimumMarginCurrentSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/minimum-margin/current-settings`,
        method: 'PATCH',
      }),
      invalidatesTags: invalidateOnSuccess(
        (_result, queryArg: PatchMinimumMarginCurrentSettingsApiArg) =>
          getMinimumMarginInvalidatedTags(queryArg)
      ),
    }),
  }),
});

export const {
  usePostServiceOrderMutation,
  useGetServiceOrderQuery,
  useLazyGetServiceOrderQuery,
  usePatchServiceOrderMutation,
  useGetServiceOrderCommercialQuery,
  usePatchServiceOrderCommercialMutation,
  useGetServiceOrderComplaintQuery,
  usePatchServiceOrderComplaintMutation,
  useGetServiceOrderContractQuery,
  usePatchServiceOrderContractMutation,
  useGetServiceOrderInsuranceQuery,
  usePatchServiceOrderInsuranceMutation,
  useGetServiceOrderInternalQuery,
  usePatchServiceOrderInternalMutation,
  usePatchServiceOrderStateMutation,
  useGetServiceOrderWarrantyQuery,
  usePatchServiceOrderWarrantyMutation,
  usePostServiceOrderChangeVariantCheckQuery,
  usePostServiceOrderChangeVariantMutation,
  useGetOrderItemsTotalAmountQuery,
  useDeleteServiceOrderItemsMutation,
  usePostServiceOrderCalculatePriceBulkMutation,
  useGetOrderProfitabilityQuery,
  usePutServiceOrderIntegrationMutation,
  useGetServiceOrderIntegrationsQuery,
  useGetServiceOrderTimeTrackingQuery,
  useGetServiceOrderMandatoryFieldsQuery,
  usePostServiceRequestCopyToServiceOrderMutation,
  usePostServiceItemsLabourSettingsMutation,
  usePostServiceItemsMaterialPartRequestMutation,
  usePostServiceItemsMaterialPartIssuedMutation,
  useGetServiceOrderTypesQuery,
  useGetServiceOrderCheckoutRestrictionQuery,
  usePostServiceItemsMaterialPartRequestCancellationMutation,
  usePostServiceItemsMaterialPartReturnMutation,
  useGetServiceCaseServiceOrderMissingMandatoryFieldsApiQuery,
  useGetServiceCaseServiceOrderMandatoryFieldsV2ApiQuery,
  useGetPartMovementsCountQuery,
  useGetSmallUnitMaterialCustomRecalculationQuery,
  usePatchSmallUnitMaterialCustomRecalculationMutation,
  useGetMinimumMarginCustomRecalculationQuery,
  useLazyGetMinimumMarginCustomRecalculationQuery,
  usePatchMinimumMarginCustomRecalculationMutation,
  usePatchMinimumMarginCurrentSettingsMutation,
} = metadaWorkshopServiceOrderApi;
